import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, ExternalLink } from 'lucide-react';
import { WebSearchData } from '@/types/webSearch';
import { supabase } from '@/integrations/supabase/client';

interface ExtractionDataModalProps {
  searchData: WebSearchData;
  defaultTab?: 'deep-research' | 'crawl' | 'scraped-pages';
  entityId?: string;
}

const JsonBlock: React.FC<{ data: any; label: string }> = ({ data, label }) => {
  if (!data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        No {label} data available. Run the process to generate data.
      </div>
    );
  }

  return (
    <ScrollArea className="h-[60vh]">
      <pre className="text-xs font-mono whitespace-pre-wrap break-words p-4 rounded-lg bg-muted">
        {JSON.stringify(data, null, 2)}
      </pre>
    </ScrollArea>
  );
};

const ScrapedPagesList: React.FC<{ entityId?: string }> = ({ entityId }) => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) { setLoading(false); return; }

    const fetchJobs = async () => {
      const { data } = await supabase
        .from('enrichment_jobs')
        .select('id, job_type, status, total_pages, pages_scraped, selected_urls, created_at')
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
        .limit(10);
      setJobs(data || []);
      setLoading(false);
    };
    fetchJobs();
  }, [entityId]);

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Loading...</div>;
  }

  if (jobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        No enrichment jobs found. Run Deep Research or Website Crawl to see scraped pages.
      </div>
    );
  }

  return (
    <ScrollArea className="h-[60vh]">
      <div className="space-y-4 p-1">
        {jobs.map((job) => {
          const urls = (job.selected_urls as string[]) || [];
          return (
            <div key={job.id} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted">
                    {job.job_type === 'deep_research' ? 'Deep Research' : 'Website Crawl'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(job.created_at).toLocaleDateString()} · {job.status}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {urls.length} pages selected · {job.pages_scraped}/{job.total_pages} scraped
                </span>
              </div>
              {urls.length > 0 ? (
                <div className="space-y-1">
                  {urls.map((url: string, i: number) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs group">
                      <span className="text-muted-foreground w-5 text-right shrink-0">{i + 1}.</span>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 hover:underline truncate flex-1"
                        title={url}
                      >
                        {url.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No URL list recorded (job created before this feature).
                </p>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};

const ExtractionDataModal: React.FC<ExtractionDataModalProps> = ({ searchData, defaultTab, entityId }) => {
  const [open, setOpen] = useState(false);

  const deepResearch = searchData?.deepResearch || null;
  const crawlAnalysis = searchData?.crawlAnalysis || null;

  const hasAnyData = deepResearch || crawlAnalysis || entityId;

  if (!hasAnyData) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
          <FileText className="h-3.5 w-3.5 mr-1" />
          View Raw Data
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>AI Extraction Data</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue={defaultTab || (entityId ? 'scraped-pages' : deepResearch ? 'deep-research' : 'crawl')}>
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="scraped-pages">
              Scraped Pages
            </TabsTrigger>
            <TabsTrigger value="deep-research">
              Deep Research
            </TabsTrigger>
            <TabsTrigger value="crawl">
              Website Crawl
            </TabsTrigger>
          </TabsList>
          <TabsContent value="scraped-pages" className="mt-4">
            <ScrapedPagesList entityId={entityId} />
          </TabsContent>
          <TabsContent value="deep-research" className="mt-4">
            <JsonBlock data={deepResearch} label="deep research" />
          </TabsContent>
          <TabsContent value="crawl" className="mt-4">
            <JsonBlock data={crawlAnalysis} label="crawl" />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ExtractionDataModal;
